import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://app.qlicxo.com/api/v1';

export default function ReviewApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resultStatus, setResultStatus] = useState('');
  const [voiceData, setVoiceData] = useState(null);

  // Override viewport for mobile responsiveness
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const original = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5');
    }
    return () => {
      if (viewport && original) viewport.setAttribute('content', original);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/review/${token}`)
      .then(res => {
        const d = res.data.data;
        setData(d);
        if (d.status === 'Approved' || d.status === 'Rejected') {
          setSubmitted(true);
          setResultStatus(d.status);
        }
      })
      .catch(() => setError('This review link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (selectedAction) => {
    if (selectedAction === 'reject' && !reason.trim()) {
      alert('Please provide a reason for the revision request.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/review/${token}`, {
        action: selectedAction,
        reason: selectedAction === 'reject' ? reason : null,
        voice_data: voiceData || null,
      });
      setSubmitted(true);
      setResultStatus(res.data.status);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="ra-page"><style>{responsiveCSS}</style>
      <div className="ra-card"><p className="ra-loading">Loading...</p></div>
    </div>
  );

  if (error && !data) return (
    <div className="ra-page"><style>{responsiveCSS}</style>
      <div className="ra-card">
        <div className="ra-brand"><div className="ra-logo">Q</div><span className="ra-brand-name">Qlicxo</span></div>
        <p className="ra-error">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="ra-page">
      <style>{responsiveCSS}</style>
      <div className="ra-card">
        {/* Header */}
        <div className="ra-brand">
          <div className="ra-logo">Q</div>
          <div>
            <div className="ra-brand-name">Qlicxo</div>
            <div className="ra-tagline">Content Review</div>
          </div>
        </div>

        {/* Status banner */}
        {submitted && (
          <div className={`ra-banner ${resultStatus === 'Approved' ? 'ra-banner-approved' : 'ra-banner-rejected'}`}>
            {resultStatus === 'Approved' ? '✓ Approved successfully' : '↩ Revision requested'}
          </div>
        )}

        {/* Item header */}
        <h2 className="ra-title">{data.wo_title || 'Review Item'}</h2>
        <p className="ra-subtitle">
          Hi <strong>{data.client_name}</strong> — {submitted
            ? `you have ${resultStatus === 'Approved' ? 'approved' : 'requested a revision for'} this item.`
            : `please review the ${data.module} content below.`
          }
        </p>

        {/* Meta info */}
        <div className="ra-meta">
          {data.wo_number && <span className="ra-meta-tag ra-meta-wo">{data.wo_number}</span>}
          <span className="ra-meta-tag ra-meta-module">{data.module}</span>
          {data.event_date && <span className="ra-meta-tag ra-meta-date">📅 {data.event_date}</span>}
          {(data.platforms || []).map(p => (
            <span key={p} className="ra-meta-tag ra-meta-platform">{p}</span>
          ))}
        </div>

        {/* Content detail — varies by module */}
        <div className="ra-content">
          {renderDetail(data.module_key, data.detail)}
        </div>

        {/* Version history */}
        {data.versions && data.versions.length > 0 && (
          <div className="ra-versions">
            <div className="ra-section-label">History</div>
            {data.versions.map((v, i) => (
              <div key={i} className="ra-version-row">
                <span className="ra-version-action">{v.action}</span>
                <span className="ra-version-by">{v.by}</span>
                {v.at && <span className="ra-version-date">{new Date(v.at).toLocaleDateString()}</span>}
                {v.rejection_reason && <div className="ra-version-reason">Reason: {v.rejection_reason}</div>}
                {v.rejectionReason && <div className="ra-version-reason">Reason: {v.rejectionReason}</div>}
                {v.voiceNote?.audio && (
                  <div style={{ width: '100%', marginTop: 4 }}>
                    <audio controls src={v.voiceNote.audio} style={{ height: '28px', maxWidth: '260px' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && <p className="ra-error">{error}</p>}

        {/* Action buttons */}
        {!submitted && (
          <div className="ra-actions">
            {/* Voice feedback recorder */}
            <VoiceRecorder onRecorded={setVoiceData} />

            {action === 'reject' ? (
              <>
                <div className="ra-section-label">Reason for revision</div>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Please describe what changes you'd like..."
                  maxLength={2000}
                  className="ra-textarea"
                  rows={4}
                />
                <div className="ra-btn-row">
                  <button
                    onClick={() => handleSubmit('reject')}
                    disabled={submitting || !reason.trim()}
                    className="ra-btn ra-btn-reject"
                    style={{ opacity: (submitting || !reason.trim()) ? 0.5 : 1 }}
                  >
                    {submitting ? 'Submitting...' : '↩ Submit Revision Request'}
                  </button>
                  <button onClick={() => { setAction(null); setReason(''); setVoiceData(null); }} className="ra-btn ra-btn-back">
                    ← Back
                  </button>
                </div>
              </>
            ) : (
              <div className="ra-btn-row">
                <button
                  onClick={() => handleSubmit('approve')}
                  disabled={submitting}
                  className="ra-btn ra-btn-approve"
                  style={{ opacity: submitting ? 0.5 : 1 }}
                >
                  {submitting ? 'Approving...' : '✅ Approve'}
                </button>
                <button
                  onClick={() => setAction('reject')}
                  disabled={submitting}
                  className="ra-btn ra-btn-reject"
                >
                  ↩ Request Revision
                </button>
              </div>
            )}
          </div>
        )}

        <p className="ra-footer">Powered by Qlicxo WorkOps</p>
      </div>
    </div>
  );
}

// ── Detail renderers per module ───────────────────────────────────────────

function renderDetail(moduleKey, detail) {
  if (!detail) return <p className="ra-empty">No content available.</p>;

  switch (moduleKey) {
    case 'postcopy':
      return (
        <>
          {detail.caption && <DetailSection label="Caption" value={detail.caption} />}
          {detail.hashtags && <DetailSection label="Hashtags" value={detail.hashtags} />}
          {detail.link && <DetailLink label="Reference Link" url={detail.link} />}
          {detail.suggested_time && <DetailSection label="Suggested Time" value={detail.suggested_time} />}
          {detail.platform_copies && detail.platform_copies.length > 0 && (
            <div className="ra-detail-section">
              <div className="ra-detail-label">Platform Copies</div>
              {detail.platform_copies.map((pc, i) => (
                <div key={i} className="ra-platform-copy">
                  <strong>{pc.platform || `Copy ${i + 1}`}</strong>
                  <p>{pc.caption || pc.text || pc.copy || JSON.stringify(pc)}</p>
                </div>
              ))}
            </div>
          )}
        </>
      );

    case 'creatives':
      return (
        <>
          {detail.link && <DetailLink label="Creative Link" url={detail.link} />}
          {detail.image_data && (
            <div className="ra-detail-section">
              <div className="ra-detail-label">Creative Preview</div>
              <img src={detail.image_data} alt={detail.image_name || 'Creative'} className="ra-image" />
            </div>
          )}
          {detail.notes && <DetailSection label="Notes" value={detail.notes} />}
          {detail.voice_data && <DetailAudio label="Designer Voice Note" src={detail.voice_data} />}
          {detail.fb_voice_data && <DetailAudio label="Voice Feedback" src={detail.fb_voice_data} />}
        </>
      );

    case 'content':
      return (
        <>
          {detail.hook && <DetailSection label="Hook" value={detail.hook} />}
          {detail.script && <DetailSection label="Script" value={detail.script} />}
          {detail.message && <DetailSection label="Message" value={detail.message} />}
          {detail.caption && <DetailSection label="Caption" value={detail.caption} />}
          {detail.hashtags && <DetailSection label="Hashtags" value={detail.hashtags} />}
          {detail.cta && <DetailSection label="Call to Action" value={detail.cta} />}
          {detail.visual && <DetailSection label="Visual Direction" value={detail.visual} />}
        </>
      );

    case 'videoworks':
      return (
        <>
          {detail.work_type && <DetailSection label="Type" value={detail.work_type} />}
          {detail.brief && <DetailSection label="Brief" value={detail.brief} />}
          {detail.concept && <DetailSection label="Concept" value={detail.concept} />}
          {detail.draft_edit_link && <DetailLink label="Draft/Edit Link" url={detail.draft_edit_link} />}
          {detail.final_link && <DetailLink label="Final Video" url={detail.final_link} />}
          {detail.thumbnail_url && (
            <div className="ra-detail-section">
              <div className="ra-detail-label">Thumbnail</div>
              <img src={detail.thumbnail_url} alt="Thumbnail" className="ra-image" />
            </div>
          )}
          {detail.fb_voice_data && <DetailAudio label="Voice Feedback" src={detail.fb_voice_data} />}
        </>
      );

    default:
      return <p className="ra-empty">Content details are not available for this module.</p>;
  }
}

function DetailSection({ label, value }) {
  return (
    <div className="ra-detail-section">
      <div className="ra-detail-label">{label}</div>
      <div className="ra-detail-value">{value}</div>
    </div>
  );
}

function DetailAudio({ label, src }) {
  return (
    <div className="ra-detail-section">
      <div className="ra-detail-label">{label}</div>
      <audio controls src={src} style={{ height: '36px', maxWidth: '100%' }} />
    </div>
  );
}

function DetailLink({ label, url }) {
  return (
    <div className="ra-detail-section">
      <div className="ra-detail-label">{label}</div>
      <a href={url} target="_blank" rel="noopener noreferrer" className="ra-link">{url}</a>
    </div>
  );
}

// ── Voice Recorder component ──────────────────────────────────────────────

function VoiceRecorder({ onRecorded }) {
  const [state, setState] = useState('idle'); // idle | recording | recorded
  const [audioUrl, setAudioUrl] = useState(null);
  const [timer, setTimer] = useState(0);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timerRef = React.useRef(null);

  const fmtTime = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
      });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        const reader = new FileReader();
        reader.onload = ev => { onRecorded(ev.target.result); };
        reader.readAsDataURL(blob);
        setState('recorded');
        clearInterval(timerRef.current);
      };
      mr.start();
      mediaRef.current = mr;
      setState('recording');
      setTimer(0);
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch (e) {
      alert('Microphone access denied. Please allow microphone access to record voice feedback.');
    }
  };

  const stop = () => { mediaRef.current?.stop(); clearInterval(timerRef.current); };

  const discard = () => {
    setAudioUrl(null); setTimer(0); setState('idle');
    onRecorded(null);
  };

  return (
    <div className="ra-voice">
      {state === 'idle' && (
        <button onClick={start} className="ra-voice-btn ra-voice-start">
          <span className="ra-voice-dot" /> Add voice feedback (optional)
        </button>
      )}
      {state === 'recording' && (
        <div className="ra-voice-row">
          <div className="ra-voice-recording">
            <span className="ra-voice-dot ra-voice-pulse" />
            <span className="ra-voice-timer">Recording {fmtTime(timer)}</span>
          </div>
          <button onClick={stop} className="ra-voice-btn ra-voice-stop">⏹ Stop</button>
        </div>
      )}
      {state === 'recorded' && (
        <div className="ra-voice-row">
          <audio controls src={audioUrl} className="ra-voice-audio" />
          <button onClick={discard} className="ra-voice-btn ra-voice-discard">✕ Remove</button>
        </div>
      )}
    </div>
  );
}

// ── Responsive CSS ────────────────────────────────────────────────────────

const responsiveCSS = `
  .ra-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f0f4ff 0%, #e8ecf8 100%);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-sizing: border-box;
  }
  .ra-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    padding: 32px;
    width: 100%;
    max-width: 640px;
    box-sizing: border-box;
  }
  .ra-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }
  .ra-logo {
    width: 40px; height: 40px; border-radius: 10px;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 20px; flex-shrink: 0;
  }
  .ra-brand-name { font-weight: 700; font-size: 18px; color: #1e293b; }
  .ra-tagline { font-size: 12px; color: #64748b; }

  .ra-banner {
    border-radius: 8px; padding: 12px 16px;
    font-weight: 600; font-size: 14px; margin-bottom: 16px;
  }
  .ra-banner-approved { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
  .ra-banner-rejected { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }

  .ra-title { font-size: 22px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
  .ra-subtitle { font-size: 14px; color: #475569; line-height: 1.5; margin: 0 0 16px; }

  .ra-meta {
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 20px;
  }
  .ra-meta-tag {
    font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px;
  }
  .ra-meta-wo { background: #ede9fe; color: #5b21b6; }
  .ra-meta-module { background: #dbeafe; color: #1e40af; }
  .ra-meta-date { background: #fef3c7; color: #92400e; }
  .ra-meta-platform { background: #f1f5f9; color: #475569; }

  .ra-content {
    background: #f8fafc; border-radius: 12px; padding: 20px;
    margin-bottom: 20px; border: 1px solid #e2e8f0;
  }
  .ra-detail-section { margin-bottom: 16px; }
  .ra-detail-section:last-child { margin-bottom: 0; }
  .ra-detail-label {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 4px;
  }
  .ra-detail-value {
    font-size: 14px; color: #334155; line-height: 1.6; white-space: pre-wrap;
  }
  .ra-link {
    font-size: 14px; color: #3b82f6; text-decoration: none; word-break: break-all;
  }
  .ra-link:hover { text-decoration: underline; }
  .ra-image {
    max-width: 100%; border-radius: 8px; margin-top: 8px;
    border: 1px solid #e2e8f0;
  }
  .ra-platform-copy {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
    padding: 10px 14px; margin-top: 6px;
  }
  .ra-platform-copy strong { font-size: 12px; color: #64748b; }
  .ra-platform-copy p { font-size: 14px; color: #334155; margin: 4px 0 0; }
  .ra-empty { font-size: 14px; color: #94a3b8; text-align: center; }

  .ra-versions {
    margin-bottom: 20px;
  }
  .ra-section-label {
    font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 8px;
  }
  .ra-version-row {
    display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
    padding: 8px 12px; background: #f8fafc; border-radius: 6px;
    margin-bottom: 4px; font-size: 13px;
  }
  .ra-version-action { font-weight: 600; color: #1e293b; }
  .ra-version-by { color: #64748b; }
  .ra-version-date { color: #94a3b8; font-size: 12px; }
  .ra-version-reason {
    width: 100%; font-size: 12px; color: #ef4444; margin-top: 2px;
  }

  .ra-actions { margin-top: 24px; }
  .ra-textarea {
    width: 100%; padding: 12px 14px; border-radius: 8px;
    border: 1.5px solid #e2e8f0; font-size: 16px; font-family: inherit;
    resize: vertical; outline: none; box-sizing: border-box;
    margin-bottom: 12px; -webkit-appearance: none;
  }
  .ra-textarea:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }

  .ra-btn-row {
    display: flex; gap: 10px; flex-wrap: wrap;
  }
  .ra-btn {
    flex: 1; min-width: 140px; padding: 14px 20px; border-radius: 12px;
    border: none; font-size: 15px; font-weight: 700; cursor: pointer;
    transition: transform 0.1s ease; -webkit-appearance: none;
  }
  .ra-btn:active { transform: scale(0.98); }
  .ra-btn-approve {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #fff;
  }
  .ra-btn-reject {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: #fff;
  }
  .ra-btn-back {
    background: #f1f5f9; color: #475569; flex: 0;
    min-width: auto; padding: 14px 20px;
  }

  .ra-voice { margin-bottom: 16px; }
  .ra-voice-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 8px; cursor: pointer;
    font-size: 13px; font-weight: 600; transition: all 0.15s;
    -webkit-appearance: none;
  }
  .ra-voice-start {
    background: #f8fafc; border: 1px dashed #cbd5e1; color: #64748b;
  }
  .ra-voice-start:hover { background: #f1f5f9; border-color: #94a3b8; }
  .ra-voice-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #ef4444;
    display: inline-block; flex-shrink: 0;
  }
  .ra-voice-pulse { animation: raPulse 1.2s ease-in-out infinite; }
  @keyframes raPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
    50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
  }
  .ra-voice-row {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .ra-voice-recording {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 12px; background: rgba(239,68,68,0.05);
    border: 1px solid #ef4444; border-radius: 8px;
  }
  .ra-voice-timer { font-size: 13px; font-weight: 700; color: #ef4444; }
  .ra-voice-stop {
    background: #ef4444; color: #fff; border: none;
    padding: 6px 14px; font-size: 13px;
  }
  .ra-voice-audio { height: 36px; flex: 1; min-width: 160px; max-width: 300px; }
  .ra-voice-discard {
    background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0;
    padding: 6px 12px; font-size: 12px;
  }

  .ra-error { color: #ef4444; font-size: 14px; margin-top: 12px; }
  .ra-loading { text-align: center; color: #64748b; font-size: 16px; }
  .ra-footer {
    text-align: center; font-size: 11px; color: #94a3b8;
    margin-top: 24px; padding-top: 16px; border-top: 1px solid #f1f5f9;
  }

  /* ── Mobile (≤480px) ─────────────────────────────────── */
  @media (max-width: 480px) {
    .ra-page { padding: 0; align-items: stretch; background: #fff; }
    .ra-card { border-radius: 0; box-shadow: none; padding: 24px 20px; max-width: 100%; }
    .ra-title { font-size: 20px; }
    .ra-content { padding: 16px; }
    .ra-btn { padding: 16px; font-size: 16px; }
    .ra-textarea { font-size: 16px; padding: 14px; }
  }

  /* ── Tablet (481px – 768px) ──────────────────────────── */
  @media (min-width: 481px) and (max-width: 768px) {
    .ra-page { padding: 24px 16px; }
    .ra-card { padding: 28px 24px; }
  }
`;
